import { useState, useEffect, useMemo } from 'react';
import { collection, getDocs, query, where, orderBy, limit } from 'firebase/firestore';
import { db, auth } from '../firebase/config';
import { Link } from 'react-router-dom';
import { formatCurrency, toDate, runRecurringCheck } from '../utils/invoiceUtils';

function TotalIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <path d="M12 6v6l4 2" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

function AlertIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <path d="M12 9v4M12 17h.01" />
    </svg>
  );
}

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function buildRevenueByMonth(invoices) {
  const now = new Date();
  const months = [];
  for (let i = 11; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push({ key: `${d.getFullYear()}-${d.getMonth()}`, label: MONTH_LABELS[d.getMonth()], total: 0 });
  }

  const monthMap = new Map(months.map(m => [m.key, m]));

  invoices.forEach(invoice => {
    let payments = [];
    if (invoice.paymentHistory && invoice.paymentHistory.length > 0) {
      payments = invoice.paymentHistory.map(p => ({ amount: Number(p.amount) || 0, date: toDate(p.date) }));
    } else if (invoice.status === 'paid' || invoice.status === 'partial') {
      const paidAt = toDate(invoice.paidDate) || toDate(invoice.createdAt);
      if (paidAt) payments = [{ amount: Number(invoice.amountPaid || invoice.total) || 0, date: paidAt }];
    }

    payments.forEach(payment => {
      const date = payment.date;
      if (!date) return;
      const key = `${date.getFullYear()}-${date.getMonth()}`;
      const month = monthMap.get(key);
      if (month) month.total += payment.amount;
    });
  });

  return months;
}

function outstandingAmount(invoice) {
  if (invoice.status === 'partial') return Number(invoice.remainingBalance) || 0;
  if (invoice.status === 'overdue' || invoice.status === 'pending') return Number(invoice.total) || 0;
  return 0;
}

function buildAgingReport(invoices) {
  const now = new Date();
  const buckets = [
    { key: 'current', label: 'Current / 0-30 days', total: 0 },
    { key: '30', label: '31-60 days', total: 0 },
    { key: '60', label: '61-90 days', total: 0 },
    { key: '90', label: '90+ days', total: 0 }
  ];

  invoices
    .filter(invoice => invoice.status === 'pending' || invoice.status === 'overdue' || invoice.status === 'partial')
    .forEach(invoice => {
      const reference = toDate(invoice.dueDate) || toDate(invoice.createdAt);
      const amount = outstandingAmount(invoice);
      if (!reference) {
        buckets[0].total += amount;
        return;
      }
      const days = Math.max(0, Math.floor((now - reference) / (1000 * 60 * 60 * 24)));
      if (days <= 30) buckets[0].total += amount;
      else if (days <= 60) buckets[1].total += amount;
      else if (days <= 90) buckets[2].total += amount;
      else buckets[3].total += amount;
    });

  return buckets;
}

function buildTopClients(invoices) {
  const totals = new Map();
  invoices.forEach(invoice => {
    const name = invoice.clientName || invoice.customerName || 'Unknown';
    totals.set(name, (totals.get(name) || 0) + (Number(invoice.total) || 0));
  });

  return Array.from(totals.entries())
    .map(([name, total]) => ({ name, total }))
    .sort((a, b) => b.total - a.total)
    .slice(0, 5);
}

function StatCard({ icon, title, value, className = '', valueClass = '' }) {
  return (
    <div className="stat-card">
      <div className="stat-card-header">
        <span className={`stat-card-icon ${className}`}>{icon}</span>
        <h2 className="stat-title">{title}</h2>
      </div>
      <p className={`stat-value ${valueClass}`}>{value}</p>
    </div>
  );
}

export default function Dashboard() {
  const [recentInvoices, setRecentInvoices] = useState([]);
  const [allInvoices, setAllInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recurringChecked, setRecurringChecked] = useState(false);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const q = query(
          collection(db, 'invoices'),
          where('userId', '==', auth.currentUser.uid),
          orderBy('createdAt', 'desc'),
          limit(5)
        );

        const invoiceSnapshot = await getDocs(q);
        const invoiceList = invoiceSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        setRecentInvoices(invoiceList);

        const allQ = query(
          collection(db, 'invoices'),
          where('userId', '==', auth.currentUser.uid),
          orderBy('createdAt', 'desc')
        );
        const allSnapshot = await getDocs(allQ);
        const allList = allSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        setAllInvoices(allList);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  useEffect(() => {
    if (recurringChecked) return;
    setRecurringChecked(true);
    runRecurringCheck().catch(() => {});
  }, [recurringChecked]);

  const metrics = useMemo(() => {
    let paidAmount = 0;
    let receivables = 0;
    let overdue = 0;

    allInvoices.forEach(invoice => {
      if (invoice.status === 'paid') {
        paidAmount += invoice.amountPaid || invoice.total || 0;
      }
      receivables += outstandingAmount(invoice);
      if (invoice.status === 'overdue') overdue += outstandingAmount(invoice);
    });

    return {
      totalInvoices: allInvoices.length,
      paidAmount,
      receivables,
      overdue
    };
  }, [allInvoices]);

  const revenueByMonth = useMemo(() => buildRevenueByMonth(allInvoices), [allInvoices]);
  const agingReport = useMemo(() => buildAgingReport(allInvoices), [allInvoices]);
  const topClients = useMemo(() => buildTopClients(allInvoices), [allInvoices]);

  const maxRevenue = Math.max(...revenueByMonth.map(m => m.total), 1);

  if (loading) return (
    <div className="dashboard-container">
      <div className="loading-container">
        <div className="loading-spinner"></div>
      </div>
    </div>
  );

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1 className="dashboard-title">Dashboard</h1>
      </div>

      <div className="dashboard-stats">
        <StatCard
          icon={<TotalIcon />}
          title="Total Invoices"
          value={metrics.totalInvoices}
          className="stat-card-icon-blue"
        />
        <StatCard
          icon={<ClockIcon />}
          title="Receivables"
          value={formatCurrency(metrics.receivables)}
          className="stat-card-icon-amber"
          valueClass="stat-pending"
        />
        <StatCard
          icon={<AlertIcon />}
          title="Overdue"
          value={formatCurrency(metrics.overdue)}
          className="stat-card-icon-red"
          valueClass="stat-overdue"
        />
        <StatCard
          icon={<CheckIcon />}
          title="Paid Amount"
          value={formatCurrency(metrics.paidAmount)}
          className="stat-card-icon-green"
          valueClass="stat-paid"
        />
      </div>

      <div className="dashboard-analytics-grid">
        <div className="dashboard-panel">
          <div className="dashboard-section-header">
            <h2 className="dashboard-section-title">Revenue (Last 12 Months)</h2>
          </div>
          {allInvoices.length === 0 ? (
            <div className="dashboard-empty-state">
              <p>No revenue data yet.</p>
            </div>
          ) : (
            <div className="dashboard-chart">
              {revenueByMonth.map(month => (
                <div className="dashboard-chart-item" key={month.key} title={`${month.label}: ${formatCurrency(month.total)}`}>
                  <div className="dashboard-chart-track">
                    <div
                      className="dashboard-chart-bar"
                      style={{ height: `${Math.max((month.total / maxRevenue) * 100, 2)}%` }}
                    ></div>
                  </div>
                  <span className="dashboard-chart-label">{month.label}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="dashboard-panel">
          <div className="dashboard-section-header">
            <h2 className="dashboard-section-title">Aging Report</h2>
          </div>
          {agingReport.every(b => b.total === 0) ? (
            <div className="dashboard-empty-state">
              <p>Nothing outstanding. Nice!</p>
            </div>
          ) : (
            <div className="dashboard-aging">
              {agingReport.map(bucket => (
                <div className="dashboard-aging-row" key={bucket.key}>
                  <span>{bucket.label}</span>
                  <strong>{formatCurrency(bucket.total)}</strong>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="dashboard-panel">
          <div className="dashboard-section-header">
            <h2 className="dashboard-section-title">Top Clients</h2>
            <Link to="/clients" className="view-all-link">Manage</Link>
          </div>
          {topClients.length === 0 ? (
            <div className="dashboard-empty-state">
              <p>No clients yet.</p>
            </div>
          ) : (
            <div className="dashboard-top-clients">
              {topClients.map((client, index) => (
                <div className="dashboard-top-client-row" key={client.name}>
                  <span className="dashboard-top-client-rank">{index + 1}</span>
                  <Link
                    to={`/invoices?client=${encodeURIComponent(client.name)}`}
                    className="dashboard-top-client-name"
                  >
                    {client.name}
                  </Link>
                  <strong>{formatCurrency(client.total)}</strong>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="dashboard-panel">
        <div className="dashboard-section-header">
          <h2 className="dashboard-section-title">Recent Invoices</h2>
          <Link to="/invoices" className="view-all-link">View All</Link>
        </div>

        {recentInvoices.length === 0 ? (
          <div className="dashboard-empty-state">
            <p>No invoices found. <Link to="/create-invoice">Create your first invoice</Link></p>
          </div>
        ) : (
          <div className="dashboard-recent-invoices">
            <table>
              <thead>
                <tr>
                  <th>Invoice #</th>
                  <th>Client</th>
                  <th>Amount</th>
                  <th>Date</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {recentInvoices.map(invoice => (
                  <tr key={invoice.id}>
                    <td data-label="Invoice #">
                      <Link to={`/invoice/${invoice.id}`} className="dashboard-invoice-id">
                        {invoice.invoiceNumber || invoice.invoiceNo}
                      </Link>
                    </td>
                    <td data-label="Client">{invoice.clientName || invoice.customerName}</td>
                    <td data-label="Amount" className="dashboard-invoice-amount">{formatCurrency(invoice.total)}</td>
                    <td data-label="Date">
                      {invoice.createdAt && invoice.createdAt.toDate ? 
                        invoice.createdAt.toDate().toLocaleDateString() : 'N/A'}
                    </td>
                    <td data-label="Status">
                      <span className={`dashboard-invoice-status ${
                        invoice.status === 'paid' 
                          ? 'status-paid' 
                          : invoice.status === 'pending' 
                          ? 'status-pending' 
                          : invoice.status === 'partial'
                          ? 'status-partial'
                          : 'status-overdue'
                      }`}>
                        {invoice.status === 'partial' ? 'Partial' : invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                      </span>
                    </td>
                    <td className="responsive-actions-cell" data-label="Actions">
                      <div className="dashboard-invoice-actions">
                        <Link 
                          to={`/invoice/${invoice.id}`}
                          className="dashboard-action-btn dashboard-view-btn"
                        >
                          View
                        </Link>
                        <Link 
                          to={`/edit-invoice/${invoice.id}`}
                          className="dashboard-action-btn dashboard-edit-btn"
                        >
                          Edit
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}